import Phaser from 'phaser';
import { ComponentType, ComponentCategory } from '@/components/ComponentDefs';
import { EventBus, GameEvents } from '@/events/EventBus';
import { COMPONENT_SPACING, ROBOT_TILE_SCALE } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import type { InstalledComponent } from '@/entities/Robot';
import type { RobotHost } from './RobotHost';

/**
 * Visual effects for a Robot: smoke, fire, break burst, wheel/turbine spin,
 * component animations (battery pulse, sensor wobble, recoil, overheat).
 */
export class RobotFX {
  /** Smoke threshold: component starts smoking below this HP ratio */
  private static readonly SMOKE_THRESHOLD = 0.7;

  /** Per-component smoke emitters for damaged modules */
  private compSmokeTimers: Map<InstalledComponent, Phaser.Time.TimerEvent> = new Map();

  // Wheel visuals for spin animation
  public wheelVisuals: (Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite)[] = [];
  public wheelsSpinning = false;

  // Turbine visuals for spin animation during flight
  public turbineVisuals: (Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite)[] = [];
  public turbinesSpinning = false;

  // Component animation state
  public compAnimsStarted = false;
  public compAnimsPaused = false;
  public recoilHandler: ((data: any) => void) | null = null;
  public overheatHandler: ((data: any) => void) | null = null;

  constructor(private host: RobotHost) {}

  /** Remove a component from wheel/turbine visual tracking (before eject) */
  removeFromVisualTracking(comp: InstalledComponent): void {
    if (comp.def.type === ComponentType.WHEEL) {
      const idx = this.wheelVisuals.indexOf(comp.graphic as any);
      if (idx >= 0) this.wheelVisuals.splice(idx, 1);
    }
    if (comp.def.type === ComponentType.TURBINE) {
      const idx = this.turbineVisuals.indexOf(comp.graphic as any);
      if (idx >= 0) this.turbineVisuals.splice(idx, 1);
    }
  }

  /** Track a visual in the correct list after creation */
  trackComponentVisual(comp: InstalledComponent, visual: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite): void {
    if (comp.def.type === ComponentType.WHEEL) {
      this.wheelVisuals.push(visual);
    }
    if (comp.def.type === ComponentType.TURBINE) {
      this.turbineVisuals.push(visual);
    }
  }

  // ── Per-component damage smoke ──

  /**
   * Update smoke state for a component after it takes damage.
   */
  updateCompSmoke(comp: InstalledComponent): void {
    if (comp.compMaxHp <= 0) return;
    const ratio = comp.compCurrentHp / comp.compMaxHp;

    if (ratio >= RobotFX.SMOKE_THRESHOLD || !this.host.scene.textures.exists('fx_smoke')) {
      this.stopCompSmoke(comp);
      return;
    }

    let interval: number;
    if (comp.broken)    interval = 80;
    else if (ratio < 0.4) interval = 200;
    else                  interval = 350;

    const existing = this.compSmokeTimers.get(comp);
    if (existing && Math.abs(existing.delay - interval) < 30) return;

    this.stopCompSmoke(comp);
    const timer = this.host.scene.time.addEvent({
      delay: interval,
      loop: true,
      callback: () => {
        this.emitCompSmokePuff(comp);
        if (comp.broken && !comp.ejected) this.emitCompFireFlicker(comp);
      },
    });
    this.compSmokeTimers.set(comp, timer);
  }

  /** Get world position of a component */
  getCompWorldPos(comp: InstalledComponent): { wx: number; wy: number } {
    const sm = GOD.componentSpacingMult;
    const s = this.host.renderScale;
    const flipX = this.host.flipped ? -1 : 1;
    const rot = this.host.container.rotation;
    const cosR = Math.cos(rot);
    const sinR = Math.sin(rot);
    const lx = (flipX * comp.localX) * sm * s;
    const ly = comp.localY * sm * s;
    return {
      wx: this.host.x + lx * cosR - ly * sinR,
      wy: this.host.y + lx * sinR + ly * cosR,
    };
  }

  /** Spawn a single smoke puff at a component's world position */
  private emitCompSmokePuff(comp: InstalledComponent): void {
    if (!this.host.alive || !this.host.scene.textures.exists('fx_smoke')) return;
    const { wx, wy } = this.getCompWorldPos(comp);

    const puff = this.host.scene.add.image(wx, wy, 'fx_smoke').setDepth(35);
    const isBroken = comp.broken && !comp.ejected;
    const baseScale = isBroken ? (0.3 + Math.random() * 0.25) : (0.25 + Math.random() * 0.2);
    puff.setScale(baseScale);
    puff.setAlpha(isBroken ? 0.85 : (0.7 + Math.random() * 0.2));
    puff.setTint(isBroken ? 0x222222 : (comp.compCurrentHp / comp.compMaxHp < 0.4 ? 0x555555 : 0xaaaaaa));

    this.host.scene.tweens.add({
      targets: puff,
      x: wx + (Math.random() - 0.5) * 10,
      y: wy - 14 - Math.random() * 14,
      scaleX: baseScale * 3,
      scaleY: baseScale * 3,
      alpha: 0,
      duration: isBroken ? 600 + Math.random() * 400 : 500 + Math.random() * 300,
      onComplete: () => puff.destroy(),
    });
  }

  /** Spawn a fire flicker at a broken component's world position */
  private emitCompFireFlicker(comp: InstalledComponent): void {
    if (!this.host.alive) return;
    const { wx, wy } = this.getCompWorldPos(comp);

    const size = 2 + Math.random() * 3;
    const color = Math.random() < 0.4 ? 0xffdd44 : 0xff6600;
    const flame = this.host.scene.add.rectangle(
      wx + (Math.random() - 0.5) * 6,
      wy + (Math.random() - 0.5) * 4,
      size, size * 1.6, color, 0.85,
    ).setDepth(36);

    this.host.scene.tweens.add({
      targets: flame,
      y: flame.y - 5 - Math.random() * 6,
      scaleX: 0.2, scaleY: 1.4 + Math.random() * 0.5,
      alpha: 0,
      duration: 200 + Math.random() * 150,
      onComplete: () => flame.destroy(),
    });
  }

  /** Initial break burst -- small flash + sparks at break moment */
  emitBreakBurst(comp: InstalledComponent): void {
    const { wx, wy } = this.getCompWorldPos(comp);
    const flash = this.host.scene.add.circle(wx, wy, 8, 0xff6600, 0.8).setDepth(37);
    this.host.scene.tweens.add({
      targets: flash,
      scaleX: 2.5, scaleY: 2.5, alpha: 0,
      duration: 200, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    for (let i = 0; i < 5; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 8 + Math.random() * 12;
      const spark = this.host.scene.add.rectangle(
        wx, wy, 2, 2, Math.random() < 0.5 ? 0xffcc00 : 0xff8800, 1,
      ).setDepth(37);
      this.host.scene.tweens.add({
        targets: spark,
        x: wx + Math.cos(angle) * dist,
        y: wy + Math.sin(angle) * dist,
        alpha: 0,
        duration: 150 + Math.random() * 150,
        onComplete: () => spark.destroy(),
      });
    }
  }

  /** Stop smoke emitter for a specific component */
  stopCompSmoke(comp: InstalledComponent): void {
    const timer = this.compSmokeTimers.get(comp);
    if (timer) {
      timer.destroy();
      this.compSmokeTimers.delete(comp);
    }
  }

  /** Stop all component smoke timers (called on destroy) */
  stopAllCompSmoke(): void {
    for (const timer of this.compSmokeTimers.values()) timer.destroy();
    this.compSmokeTimers.clear();
  }

  /** Spawn steam/smoke puff at weapon position when overheated */
  spawnOverheatSteam(comp: InstalledComponent): void {
    const scene = this.host.scene;
    if (!scene?.scene.isActive()) return;
    const s = ROBOT_TILE_SCALE;
    const wPos = this.host.getWeaponWorldPos(comp);
    for (let i = 0; i < 4; i++) {
      const ox = (Math.random() - 0.5) * 10 * s;
      const oy = (Math.random() - 0.5) * 10 * s;
      const hasTex = scene.textures.exists('fx_smoke');
      const puff = hasTex
        ? scene.add.image(wPos.x + ox, wPos.y + oy, 'fx_smoke').setTint(0xcccccc).setAlpha(0.7).setScale(s * 0.3).setDepth(100)
        : scene.add.circle(wPos.x + ox, wPos.y + oy, 3 * s, 0xcccccc, 0.7).setDepth(100);
      scene.tweens.add({
        targets: puff,
        y: (puff as any).y - 15 * s,
        scaleX: s * 1.2, scaleY: s * 1.2,
        alpha: 0,
        duration: 500 + Math.random() * 300,
        delay: i * 80,
        onComplete: () => puff.destroy(),
      });
    }
  }

  // ── Wheel & Turbine spin ──

  updateWheelSpin(): void {
    const speed = Math.sqrt(this.host.vx * this.host.vx + this.host.vy * this.host.vy);
    const isMoving = this.host.alive && speed > 1 && !this.host.energySaveMode;
    if (isMoving && !this.wheelsSpinning) {
      this.wheelsSpinning = true;
      for (let i = 0; i < this.wheelVisuals.length; i++) {
        const wv = this.wheelVisuals[i];
        const wheelComp = this.host.components.find(
          c => c.def.type === ComponentType.WHEEL && c.graphic === wv
        );
        if (wheelComp?.broken) continue;
        this.host.scene.tweens.add({
          targets: wv,
          rotation: wv.rotation + Math.PI * 2,
          duration: 600,
          repeat: -1,
          ease: 'Linear',
        });
      }
    } else if (!isMoving && this.wheelsSpinning) {
      this.wheelsSpinning = false;
      for (const wv of this.wheelVisuals) {
        this.host.scene.tweens.killTweensOf(wv);
      }
    }
  }

  updateTurbineSpin(): void {
    const shouldSpin = this.host.alive && this.host.canFly() && !this.host.isCrashing && !this.host.energySaveMode;
    if (shouldSpin && !this.turbinesSpinning) {
      this.turbinesSpinning = true;
      for (const tv of this.turbineVisuals) {
        const comp = this.host.components.find(
          c => c.def.type === ComponentType.TURBINE && c.graphic === tv
        );
        if (comp?.broken) continue;
        this.host.scene.tweens.add({
          targets: tv,
          rotation: tv.rotation + Math.PI * 2,
          duration: 300,
          repeat: -1,
          ease: 'Linear',
        });
      }
    } else if (!shouldSpin && this.turbinesSpinning) {
      this.turbinesSpinning = false;
      for (const tv of this.turbineVisuals) {
        this.host.scene.tweens.killTweensOf(tv);
      }
    }
  }

  // ── Component animations ──

  /** Start subtle idle animations on components (called once after placement) */
  startComponentAnims(): void {
    if (this.compAnimsStarted) return;
    this.compAnimsStarted = true;

    for (const comp of this.host.components) {
      if (comp.broken || !comp.graphic) continue;

      const gfx = comp.graphic;
      const baseScaleX = gfx.scaleX;
      const baseScaleY = gfx.scaleY;

      // Battery: subtle scale pulse
      if (comp.def.type === ComponentType.BATTERY) {
        comp.tween = this.host.scene.tweens.add({
          targets: gfx,
          scaleX: baseScaleX * 1.03,
          scaleY: baseScaleY * 1.03,
          duration: 1200 + Math.random() * 400,
          yoyo: true, repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }

      // Sensor: gentle rotation oscillation
      if (comp.def.category === ComponentCategory.SENSOR) {
        const baseRot = gfx.rotation;
        comp.tween = this.host.scene.tweens.add({
          targets: gfx,
          rotation: baseRot + 0.05,
          duration: 800 + Math.random() * 400,
          yoyo: true, repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }

      // Thruster: pulsating glow when robot has wings
      if (comp.def.type === ComponentType.THRUSTER && this.host.canFly()) {
        comp.tween = this.host.scene.tweens.add({
          targets: gfx,
          alpha: { from: 0.75, to: 1 },
          scaleX: { from: (gfx as any).scaleX * 0.95, to: (gfx as any).scaleX * 1.05 },
          scaleY: { from: (gfx as any).scaleY * 0.95, to: (gfx as any).scaleY * 1.05 },
          duration: 200 + Math.random() * 100,
          repeat: -1,
          yoyo: true,
          ease: 'Sine.easeInOut',
        });
      }
    }

    // Cannon recoil: listen for fire events
    this.recoilHandler = (data: { robot: any; weaponType: ComponentType }) => {
      if (data.robot !== this.host || !this.host.alive) return;
      for (const comp of this.host.components) {
        if (comp.def.type === data.weaponType && comp.graphic && !comp.broken) {
          // Store the true resting position once (before any recoil drift)
          const g = comp.graphic as any;
          if (g._recoilBaseX == null) g._recoilBaseX = g.x;
          // Kill any in-flight recoil tween to prevent position drift
          this.host.scene.tweens.killTweensOf(g);
          // Snap back to base position before starting new recoil
          g.x = g._recoilBaseX;
          const recoilDir = this.host.flipped ? 1.5 : -1.5;
          this.host.scene.tweens.add({
            targets: g,
            x: g._recoilBaseX + recoilDir,
            duration: 40,
            yoyo: true,
            ease: 'Power2',
          });
          break;
        }
      }
    };
    EventBus.on(GameEvents.ROBOT_FIRED, this.recoilHandler);

    // Overheat visual feedback for burst cannons
    this.overheatHandler = (data: { robot: any; comp: InstalledComponent; heat: number; cooling: boolean }) => {
      if (data.robot !== this.host || !data.comp.graphic) return;
      const g = data.comp.graphic as any;
      if (!g.setTint) return;
      if (data.cooling && data.heat >= 0.95) {
        g.setTint(0xff2200);
        this.spawnOverheatSteam(data.comp);
      } else if (data.cooling) {
        const r = Math.round(0xff * data.heat);
        const gb = Math.round(0x44 * (1 - data.heat));
        g.setTint((r << 16) | (gb << 8) | gb);
        if (data.heat <= 0.1 && g.clearTint) g.clearTint();
      } else {
        const intensity = data.heat;
        const r = Math.round(0xff * intensity);
        const gVal = Math.round(0x88 * (1 - intensity));
        g.setTint((r << 16) | (gVal << 8) | 0x00);
      }
    };
    EventBus.on(GameEvents.WEAPON_OVERHEAT, this.overheatHandler);
  }

  /** Cleanup event listeners (called on Robot destroy) */
  destroyListeners(): void {
    if (this.recoilHandler) {
      EventBus.off(GameEvents.ROBOT_FIRED, this.recoilHandler);
      this.recoilHandler = null;
    }
    if (this.overheatHandler) {
      EventBus.off(GameEvents.WEAPON_OVERHEAT, this.overheatHandler);
      this.overheatHandler = null;
    }
  }
}
